/* Typed fetch client. Everything is same-origin in production and proxied
   through Vite in dev, so cookies ride along without CORS gymnastics. */

export type User = { id: number; username: string; display_name: string; is_admin: boolean };

export type Member = {
  id: number;
  name: string;
  /** Email or phone. Where filled in, this is what identifies someone. */
  contact: string;
  note: string;
  /** 1 = on the member roll; 0 = a guest who has played. Events are open to all. */
  is_registered: number;
  registered_at: string | null;
  created_at: string;
  attendance: number;
};

export type Event = {
  id: number;
  name: string;
  event_date: string;
  format: string;
  winner_member_id: number | null;
  spend_isk: number;
  spend_note: string;
  income_isk: number;
  income_note: string;
  /** Scryfall set code, e.g. "fdn" — pins the banner art to that set. */
  set_code: string;
  notes: string;
  attendees: number[];
  /** 1 = listed on the public sign-up form. Nothing is public by default. */
  registration_open: number;
  /** 0 = no limit. */
  capacity: number;
};

/** A member this sign-up might already be, and why we think so. */
export type Suggestion = { id: number; name: string; why: string; score: number };

/** A public sign-up, waiting for someone on the board to accept it. */
export type Registration = {
  id: number;
  event_id: number;
  name: string;
  contact: string;
  status: 'new' | 'approved' | 'declined';
  member_id: number | null;
  created_at: string;
  /** Possible existing members, best first. Empty once handled. */
  suggestions: Suggestion[];
};

export type Todo = {
  id: number;
  text: string;
  due_date: string | null;
  done: number;
  done_at: string | null;
  /** A board member with a login. null = nobody has taken it on. */
  assigned_user_id: number | null;
  notes: string;
};

export type BoardUser = { id: number; username: string; display_name: string };

export type TournamentSummary = {
  id: number; name: string; rounds: number; finished: boolean;
  playerCount: number; created_at: string;
  deckbuild_ends_at: string | null;
  round_ends_at: string | null;
  round_minutes: number;
  /** The club night this belongs to. Its attendance follows the roster. */
  event_id: number | null;
  event_name: string | null;
};

export type TPlayer = { id: number; name: string; member_id: number; dropped: number };
export type TMatch = {
  id: number; round_no: number;
  a_player_id: number; b_player_id: number | null;
  a_games: number | null; b_games: number | null;
  /** Games drawn or left unfinished — one count for the match, not per player. */
  game_draws: number;
  /** A result a best-of-three could end on, not just a number keyed in so far. */
  decided: boolean;
};
/**
 * One row of the table, with the four official tiebreakers on it. `mw`/`gw`
 * are the player's own percentages and `omw`/`ogw` the averages over the
 * opponents they faced; all four carry the rules' 0.33 floor, so a player who
 * has won nothing shows 33% rather than 0%.
 */
export type TStanding = {
  playerId: number; name: string; dropped: boolean;
  points: number;
  wins: number; losses: number; draws: number; byes: number;
  matchesPlayed: number;
  gamePoints: number; gamesPlayed: number;
  mw: number; gw: number;
  omw: number; ogw: number;
  opponents: number[];
};
export type Tournament = TournamentSummary & {
  event_date: string | null;
  players: TPlayer[];
  matches: TMatch[];
  standings: TStanding[];
  roundsPlayed: number;
  currentRoundComplete: boolean;
  canPairNext: boolean;
};

/** A named folder of pictures. */
export type Album = {
  id: number;
  name: string;
  note: string;
  event_id: number | null;
  cover_photo_id: number | null;
  created_at: string;
  photo_count: number;
  /** Which picture to put on the tile: the chosen cover, else the newest. */
  cover: number | null;
};

export type Photo = {
  id: number;
  album_id: number;
  caption: string;
  ext: string;
  width: number;
  height: number;
  bytes: number;
  uploaded_by: number | null;
  created_at: string;
};

export type AlbumContents = { album: Album; photos: Photo[] };

export type Art = {
  card: string;
  artist: string;
  setName: string;
  setCode: string;
  scryfall: string;
  fetchedAt: string;
  pinnedTo: string | null;
} | { none: true };

export type CardSet = { id: number; name: string; code: string; release_date: string };

/** One card in a set listing, as the server trims it down from Scryfall. */
export type SetCard = {
  id: string;
  name: string;
  number: string;
  rarity: string;
  manaCost: string;
  typeLine: string;
  colors: string;
  /** Rules text; both halves of a two-faced card, split by a lone em dash. */
  oracle: string;
  /** "3/4" for a creature, loyalty for a planeswalker, else empty. */
  pt: string;
  artist: string;
  image: string | null;
};
export type SetCardsResponse = { code: string; name: string; cards: SetCard[] };

/** A poster. `fields` is whatever the named template asked for. */
export type Ad = {
  id: number;
  title: string;
  template: string;
  fields: Record<string, string>;
  event_id: number | null;
  source_ext: string;
  /** Whether the picture it was built from was kept, so it can be edited. */
  has_source: boolean;
  credit: string;
  /** The Facebook caption. Stored with the ad, never drawn on it. */
  post_text: string;
  posted_at: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
};

export type AdInput = {
  title: string;
  template: string;
  fields: Record<string, string>;
  event_id?: number | null;
  credit?: string;
  post_text?: string;
  posted_at?: string | null;
  /** data:image/png;base64,... — the composed poster */
  render?: string;
  /** The picture it was built from; null clears it, undefined keeps it. */
  source?: string | null;
};

/** A card from Scryfall, for the art picker. */
export type CardHit = {
  id: string;
  name: string;
  setName: string;
  setCode: string;
  artist: string;
  credit: string;
};

export type Asset = {
  id: number;
  name: string;
  category: string;
  quantity: number;
  condition: string;
  acquired_date: string | null;
  cost_isk: number;
  funded_by: string;
  location: string;
  note: string;
  created_at: string;
};

export type Transaction = {
  id: number;
  tx_date: string;
  description: string;
  amount_isk: number;
  category: string;
  created_at: string;
};

export type Finance = {
  balance: number;
  income: number;
  expenses: number;
  series: { date: string; balance: number; delta: number }[];
  byCategory: Record<string, number>;
  movements: number;
};

export type Gate = { value: number; target: number };

export type Stats = {
  events: number;
  members: number;
  registered: number;
  guests: number;
  uniquePeople: number;
  regulars: number;
  avgAttendance: number;
  spendTotal: number;
  openTodos: number;
  assetCount: number;
  assetValue: number;
  grantFunded: number;
  trend: { date: string; n: number }[];
  gates: { unique: Gate; regulars: Gate; average: Gate };
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(code);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });
  if (!res.ok) {
    let code = `http_${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) code = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const body = (data: unknown): RequestInit => ({ body: JSON.stringify(data) });

export const api = {
  me: () => call<{ user: User }>('/api/auth/me'),
  login: (username: string, password: string) =>
    call<{ user: User }>('/api/auth/login', { method: 'POST', ...body({ username, password }) }),
  logout: () => call<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  changePassword: (current: string, next: string) =>
    call<{ ok: true }>('/api/auth/password', { method: 'POST', ...body({ current, next }) }),

  stats: () => call<Stats>('/api/stats'),

  members: () => call<Member[]>('/api/members'),
  addMember: (data: { name: string; contact?: string; note?: string; is_registered?: boolean }) =>
    call<Member>('/api/members', { method: 'POST', ...body(data) }),
  updateMember: (
    id: number,
    data: {
      name?: string;
      contact?: string;
      note?: string;
      is_registered?: boolean;
      registered_at?: string | null;
    },
  ) =>
    call<{ ok: true }>(`/api/members/${id}`, { method: 'PATCH', ...body(data) }),
  delMember: (id: number) => call<{ ok: true }>(`/api/members/${id}`, { method: 'DELETE' }),

  events: () => call<Event[]>('/api/events'),
  addEvent: (data: Partial<Event> & { name: string; event_date: string }) =>
    call<Event>('/api/events', { method: 'POST', ...body(data) }),
  updateEvent: (id: number, data: Partial<Event>) =>
    call<Event>(`/api/events/${id}`, { method: 'PATCH', ...body(data) }),
  delEvent: (id: number) => call<{ ok: true }>(`/api/events/${id}`, { method: 'DELETE' }),

  users: () => call<BoardUser[]>('/api/users'),

  tournaments: () => call<TournamentSummary[]>('/api/mot'),
  tournament: (id: number) => call<Tournament>(`/api/mot/${id}`),
  addTournament: (d: { name: string; rounds?: number; event_id?: number | null }) =>
    call<Tournament>('/api/mot', { method: 'POST', ...body(d) }),
  updateTournament: (id: number, d: { name?: string; rounds?: number; event_id?: number | null }) =>
    call<Tournament>(`/api/mot/${id}`, { method: 'PATCH', ...body(d) }),
  delTournament: (id: number) => call<{ ok: true }>(`/api/mot/${id}`, { method: 'DELETE' }),
  deckbuild: (id: number, minutes: number) =>
    call<Tournament>(`/api/mot/${id}/deckbuild`, { method: 'POST', ...body({ minutes }) }),
  addPlayer: (id: number, memberId: number) =>
    call<Tournament>(`/api/mot/${id}/players`, { method: 'POST', ...body({ member_id: memberId }) }),
  dropPlayer: (id: number, pid: number, dropped: boolean) =>
    call<Tournament>(`/api/mot/${id}/players/${pid}/drop`, { method: 'POST', ...body({ dropped }) }),
  /** Take someone off the roster entirely. Only safe before round one. */
  delPlayer: (id: number, pid: number) =>
    call<Tournament>(`/api/mot/${id}/players/${pid}`, { method: 'DELETE' }),
  pairRound: (id: number) => call<Tournament>(`/api/mot/${id}/pair`, { method: 'POST' }),
  /** `d` is games drawn or left unfinished; they score a game point each. */
  reportMatch: (id: number, mid: number, a: number, b: number, d = 0) =>
    call<Tournament>(`/api/mot/${id}/matches/${mid}`, { method: 'POST', ...body({ a, b, d }) }),
  undoRound: (id: number) => call<Tournament>(`/api/mot/${id}/round`, { method: 'DELETE' }),
  roundClock: (id: number, minutes: number) =>
    call<Tournament>(`/api/mot/${id}/round/clock`, { method: 'POST', ...body({ minutes }) }),
  /** More time for the round in progress; the round length is left alone. */
  extendRound: (id: number, extend: number) =>
    call<Tournament>(`/api/mot/${id}/round/clock`, { method: 'POST', ...body({ extend }) }),
  drawRemaining: (id: number) =>
    call<Tournament & { drawn: number }>(`/api/mot/${id}/round/draws`, { method: 'POST' }),

  art: () => call<Art>('/api/art'),
  refreshArt: () => call<{ ok: boolean; art: Art | null }>('/api/art/refresh', { method: 'POST' }),

  todos: () => call<Todo[]>('/api/todos'),
  addTodo: (data: {
    text: string;
    due_date?: string | null;
    assigned_user_id?: number | null;
    notes?: string;
  }) =>
    call<Todo>('/api/todos', { method: 'POST', ...body(data) }),
  updateTodo: (
    id: number,
    data: {
      done?: boolean;
      text?: string;
      due_date?: string | null;
      assigned_user_id?: number | null;
      notes?: string;
    },
  ) =>
    call<Todo>(`/api/todos/${id}`, { method: 'PATCH', ...body(data) }),
  delTodo: (id: number) => call<{ ok: true }>(`/api/todos/${id}`, { method: 'DELETE' }),

  assets: () => call<Asset[]>('/api/assets'),
  addAsset: (data: Partial<Asset> & { name: string }) =>
    call<Asset>('/api/assets', { method: 'POST', ...body(data) }),
  updateAsset: (id: number, data: Partial<Asset>) =>
    call<Asset>(`/api/assets/${id}`, { method: 'PATCH', ...body(data) }),
  delAsset: (id: number) => call<{ ok: true }>(`/api/assets/${id}`, { method: 'DELETE' }),

  finance: () => call<Finance>('/api/finance'),
  transactions: () => call<Transaction[]>('/api/transactions'),
  addTransaction: (data: {
    tx_date: string;
    description: string;
    amount_isk: number;
    category: string;
  }) => call<Transaction>('/api/transactions', { method: 'POST', ...body(data) }),
  delTransaction: (id: number) =>
    call<{ ok: true }>(`/api/transactions/${id}`, { method: 'DELETE' }),

  ads: () => call<Ad[]>('/api/ads'),
  ad: (id: number) => call<Ad>(`/api/ads/${id}`),
  addAd: (data: AdInput) => call<Ad>('/api/ads', { method: 'POST', ...body(data) }),
  updateAd: (id: number, data: Partial<AdInput>) =>
    call<Ad>(`/api/ads/${id}`, { method: 'PATCH', ...body(data) }),
  delAd: (id: number) => call<{ ok: true }>(`/api/ads/${id}`, { method: 'DELETE' }),
  cardSearch: (q: string) => call<CardHit[]>(`/api/ads/cards?q=${encodeURIComponent(q)}`),

  sets: () => call<CardSet[]>('/api/sets'),
  addSet: (data: { name: string; code?: string; release_date: string }) =>
    call<CardSet>('/api/sets', { method: 'POST', ...body(data) }),
  delSet: (id: number) => call<{ ok: true }>(`/api/sets/${id}`, { method: 'DELETE' }),
  setCards: (id: number) => call<SetCardsResponse>(`/api/sets/${id}/cards`),

  registrations: (eventId: number) => call<Registration[]>(`/api/events/${eventId}/registrations`),
  /**
   * Accept a sign-up. `memberId` puts it on an existing player — any player,
   * not only a suggested one — and omitting it creates a new one. `rename`
   * carries the sign-up's name over, which is how a player recorded as
   * "Soroush" becomes "Soroush Jafari".
   */
  approveRegistration: (id: number, memberId?: number, rename = false) =>
    call<Registration[]>(`/api/registrations/${id}/approve`, {
      method: 'POST',
      ...body({ member_id: memberId ?? null, rename }),
    }),
  declineRegistration: (id: number) =>
    call<Registration[]>(`/api/registrations/${id}/decline`, { method: 'POST' }),
  delRegistration: (id: number) =>
    call<Registration[]>(`/api/registrations/${id}`, { method: 'DELETE' }),

  albums: () => call<Album[]>('/api/albums'),
  addAlbum: (d: { name: string; note?: string; event_id?: number | null }) =>
    call<Album[]>('/api/albums', { method: 'POST', ...body(d) }),
  updateAlbum: (
    id: number,
    d: { name?: string; note?: string; event_id?: number | null; cover_photo_id?: number | null },
  ) => call<Album[]>(`/api/albums/${id}`, { method: 'PATCH', ...body(d) }),
  delAlbum: (id: number) => call<Album[]>(`/api/albums/${id}`, { method: 'DELETE' }),

  albumPhotos: (id: number) => call<AlbumContents>(`/api/albums/${id}/photos`),
  /** One picture, already resized by the browser. See client/src/photos.ts. */
  addPhoto: (
    albumId: number,
    d: { image: string; thumb: string; width: number; height: number; caption?: string },
  ) => call<Photo>(`/api/albums/${albumId}/photos`, { method: 'POST', ...body(d) }),
  updatePhoto: (id: number, d: { caption?: string; album_id?: number }) =>
    call<Photo>(`/api/photos/${id}`, { method: 'PATCH', ...body(d) }),
  delPhoto: (id: number) => call<{ ok: true }>(`/api/photos/${id}`, { method: 'DELETE' }),
};

/* ── image URLs ────────────────────────────────────────────────────
   All same-origin and behind the session cookie, so they can be used
   directly as <img src> and drawn onto a canvas without tainting it. */

/* Both carry a version — pass the ad's `updated_at`. These are served
   with `private, max-age=600`, so without a key that changes when the
   picture does, the browser keeps showing the previous one for ten
   minutes after a save. */
export const adImageUrl = (id: number, v?: string): string =>
  `/api/ads/${id}/image${v ? `?v=${encodeURIComponent(v)}` : ''}`;
export const adSourceUrl = (id: number, v?: string): string =>
  `/api/ads/${id}/source${v ? `?v=${encodeURIComponent(v)}` : ''}`;
export const cardArtUrl = (id: string): string => `/api/ads/cards/${id}/art`;

/* A photo file never changes once written — a re-upload is a new row — so
   these need no cache-busting key. */
export const photoUrl = (id: number): string => `/api/photos/${id}/file`;
export const photoThumbUrl = (id: number): string => `/api/photos/${id}/thumb`;

/** Fetch a same-origin image and turn it into a data URL for saving. */
export async function urlToDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(typeof fr.result === 'string' ? fr.result : null);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Read a picked file as a data URL, rejecting anything too large. */
export function fileToDataUrl(file: File, maxBytes = 10 * 1024 * 1024): Promise<string | null> {
  if (!file.type.startsWith('image/') || file.size > maxBytes) return Promise.resolve(null);
  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => resolve(typeof fr.result === 'string' ? fr.result : null);
    fr.onerror = () => resolve(null);
    fr.readAsDataURL(file);
  });
}

/* ── small shared helpers ─────────────────────────────────────────── */

export const kr = (n: number): string => `${(n || 0).toLocaleString('is-IS')} kr.`;

export function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

/** "í dag", "á morgun", "eftir 5 daga", "2 dögum of seint". */
export function dueText(iso: string | null): string {
  const d = daysUntil(iso);
  if (d === null) return '';
  if (d < 0) {
    const n = Math.abs(d);
    return `${n} ${n === 1 ? 'degi' : 'dögum'} of seint`;
  }
  if (d === 0) return 'í dag';
  if (d === 1) return 'á morgun';
  return `eftir ${d} daga`;
}

export function initialsOf(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('is-IS', { day: 'numeric', month: 'short', year: 'numeric' });
}
